import { View } from "react-native";
import React, { useState, useEffect, useCallback } from "react";
import { useLocalSearchParams, useRouter, useNavigation } from "expo-router";
import { useAuth } from "@components/auth/useAuth";

import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { ThemedKebabMenu, IKebabActionItem } from "@/components/ThemedKebabMenu";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAnchor, editAnchor, deleteAnchor } from "@/services/firebase/anchor-functions";
import { confirmEntityDeletion } from "@/services/firebase/delete-confirmation";
import { AnchorModel, ShareModel } from "common247";
import { AnchorEditor } from "@/components/anchors/AnchorEditor";
import { share, unshare, getSharesForSource } from "@/services/firebase/share-functions";
import { navigateBackOrToList } from "@/utils/navigation";
import { entitySync } from "@/services/sync/entity-sync";

/**
 * Screen for viewing and editing an Anchor entity.
 */
export default function EditAnchorScreen(): React.JSX.Element {
  const { id } = useLocalSearchParams<{ id: string }>();
  const services = useFirebase();
  const { user } = useAuth(true);
  const router = useRouter();
  const navigation = useNavigation();

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [anchor, setAnchor] = useState<AnchorModel | null>(null);
  const [shares, setShares] = useState<ShareModel[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);

  const fetchShares = useCallback(async () => {
    if (!services || !id) return;
    setIsLoadingShares(true);
    try {
      const res = await getSharesForSource(services, { sourceId: id, sourceType: "anchor" });
      if (res.status && res.shares) {
        setShares(res.shares);
      }
    } catch (error: any) {
      console.error("[EditAnchorScreen] Error fetching shares:", error);
    } finally {
      setIsLoadingShares(false);
    }
  }, [services, id]);

  const fetchData = useCallback(async () => {
    if (!services || !id) return;
    setLoading(true);
    try {
      const res = await getAnchor(services, { anchorId: id });
      if (res.status && res.anchor) {
        setAnchor(res.anchor);
        if (res.anchor.canShare) {
          fetchShares();
        }
      } else {
        CustomAlert.alert("Error", res.error || "Anchor not found.");
        navigateBackOrToList(router, "/(tabs)/my-anchors", navigation);
      }
    } catch (error: any) {
      console.error("[EditAnchorScreen] Fetch error:", error);
      CustomAlert.alert("Error", error.message || "Failed to load anchor.");
    } finally {
      setLoading(false);
    }
  }, [services, id, router, navigation, fetchShares]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time broadcast entity sync
  useEffect(() => {
    const unsub = entitySync.subscribe((event) => {
      if (event.type === "anchor" && event.id === id) {
        fetchData();
      }
    });
    return () => unsub();
  }, [id, fetchData]);

  const handleSave = async (formData: any) => {
    if (!services || !anchor || !id) return;

    setIsSaving(true);
    try {
      const res = await editAnchor(services, {
        anchorId: id,
        ...formData,
      });

      if (res.status) {
        const updatedAnchor: AnchorModel = {
          ...(anchor || ({} as AnchorModel)),
          ...formData,
          id,
        };
        // Auto-save updates in place — do not close the edited item
        setAnchor((prev) => (prev ? { ...prev, ...formData } : updatedAnchor));
        entitySync.emit({
          type: "anchor",
          action: "update",
          id,
          data: updatedAnchor,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to save anchor.");
      }
    } catch (error: any) {
      console.error("[EditAnchorScreen] Save error:", error);
      CustomAlert.alert("Error", error.message || "Unexpected error while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!services || !id) return;

    await confirmEntityDeletion({
      services,
      entityType: "anchor",
      entityId: id,
      entityName: anchor?.name,
      onProceedDelete: async (cascadeUnlink) => {
        setIsDeleting(true);
        try {
          const res = await deleteAnchor(services, { anchorId: id, cascadeUnlink });
          if (!res.status) {
            setIsDeleting(false);
          }
          return res;
        } catch (error) {
          setIsDeleting(false);
          throw error;
        }
      },
      onSuccess: () => {
        navigateBackOrToList(router, "/(tabs)/my-anchors", navigation);
      },
      onError: () => {
        setIsDeleting(false);
      },
    });
  };

  const handleShareTarget = async (
    targetId: string,
    targetType: "contact" | "group",
    role: "viewer" | "editor"
  ) => {
    if (!services || !id) return;
    try {
      const res = await share(services, {
        sourceId: id,
        sourceType: "anchor",
        targetId,
        targetType,
        role,
      });
      if (res.status) {
        fetchShares();
      } else {
        CustomAlert.alert("Sharing Error", res.error || "Failed to share anchor.");
      }
    } catch (err: any) {
      CustomAlert.alert("Sharing Error", err.message || "Failed to share anchor.");
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        fetchShares();
      } else {
        CustomAlert.alert("Error", res.error || "Failed to revoke share.");
      }
    } catch (err: any) {
      CustomAlert.alert("Error", err.message || "Failed to revoke share.");
    }
  };

  const userRole = anchor?.userRole || "owner";
  const isOwner = userRole === "owner";
  const canEdit = isOwner || userRole === "editor";
  const isViewer = userRole === "viewer";

  const kebabActions: IKebabActionItem[] = [];
  if (anchor && canEdit) {
    kebabActions.push({
      id: "delete",
      label: "Delete Anchor",
      icon: "trash-outline",
      destructive: true,
      onPress: handleDelete,
      disabled: isDeleting,
    });
  }

  return (
    <ThemedScreen
      title={anchor ? `${isViewer ? "View" : "Edit"}: ${anchor.name}` : "Anchor Details"}
      loading={loading}
      rightComponent={
        kebabActions.length > 0 ? (
          <ThemedKebabMenu actions={kebabActions} accessibilityLabel="Anchor options" />
        ) : undefined
      }
    >
      <View style={{ flex: 1 }}>
        <ThemedProgressBar loading={loading} />
        {!loading && anchor && (
          <AnchorEditor
            anchor={anchor}
            shares={shares}
            isLoadingShares={isLoadingShares}
            currentUserId={user?.uid}
            onSave={handleSave}
            onShare={handleShareTarget}
            onUnshare={handleUnshare}
            onCancel={() => navigateBackOrToList(router, "/(tabs)/my-anchors", navigation)}
            isSaving={isSaving}
          />
        )}
      </View>
    </ThemedScreen>
  );
}
